import React from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { StatCard } from '../components/ui/modern-card';
import { StatGrid } from '../components/ui/dashboard';
import { PageTransition, FadeIn, SlideIn } from '../components/ui/animations';
import { 
  DollarSign, 
  Users, 
  Car, 
  TrendingUp,
  MapPin,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ModernDashboardView = () => {
  const { t } = useTranslation();
  const auth = useSelector(state => state.auth);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const usersdata = useSelector(state => state.usersdata);
  const settings = useSelector(state => state.settingsdata.settings);

  // Calculate stats from real data
  const todayEarnings = React.useMemo(() => {
    if (!bookinglistdata.bookings) return 0;
    const today = new Date();
    return bookinglistdata.bookings
      .filter(b => {
        const bDate = new Date(b.tripdate);
        return bDate.toDateString() === today.toDateString() && 
               (b.status === 'PAID' || b.status === 'COMPLETE');
      })
      .reduce((sum, b) => sum + parseFloat(b.convenience_fees || 0), 0);
  }, [bookinglistdata.bookings]);

  const activeDrivers = React.useMemo(() => {
    if (!usersdata.drivers) return 0;
    return usersdata.drivers.filter(d => d.driverActiveStatus === true).length;
  }, [usersdata.drivers]);

  const totalCustomers = React.useMemo(() => {
    if (!usersdata.users) return 0;
    return usersdata.users.filter(u => u.usertype === 'customer').length;
  }, [usersdata.users]);

  const stats = [
    {
      title: t('today_text') || "Today's Earnings",
      value: `${settings?.symbol || '$'} ${todayEarnings.toFixed(2)}`,
      icon: DollarSign,
      trend: "up",
      trendValue: "12% from yesterday",
      color: "success"
    },
    {
      title: t('total_cumtomer') || 'Total Customers',
      value: totalCustomers.toString(),
      icon: Users,
      trend: "up",
      trendValue: "8% this month",
      color: "primary"
    },
    {
      title: t('active_driver') || 'Active Drivers',
      value: activeDrivers.toString(),
      icon: Car,
      trend: "up",
      trendValue: "5% this week",
      color: "info"
    },
    {
      title: t('total') || 'Total Bookings',
      value: bookinglistdata.bookings?.length?.toString() || '0',
      icon: TrendingUp,
      trend: "up",
      trendValue: "15% this month",
      color: "warning"
    }
  ];

  return (
    <PageTransition className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <FadeIn className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">{t('gross_earning') || 'Dashboard'}</h1>
        <p className="text-gray-600 mt-2">
          {t('welcome_back') || 'Welcome back'}! Here's what's happening today.
        </p>
      </FadeIn>

      {/* Stats Grid */}
      <StatGrid className="mb-8">
        {stats.map((stat, index) => (
          <SlideIn key={stat.title} direction="up" delay={index * 0.1}>
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              trend={stat.trend}
              trendValue={stat.trendValue}
              color={stat.color}
            />
          </SlideIn>
        ))}
      </StatGrid>

      {/* Quick Stats Row */}
      <SlideIn direction="up" delay={0.4} className="mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">{t('quick_stats') || 'Quick Statistics'}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Completed Rides', value: bookinglistdata.bookings?.filter(b => b.status === 'COMPLETE')?.length || 0, icon: '✅' },
              { label: 'Cancelled Rides', value: bookinglistdata.bookings?.filter(b => b.status === 'CANCELLED')?.length || 0, icon: '❌' },
              { label: 'Active Rides', value: bookinglistdata.bookings?.filter(b => b.status === 'STARTED')?.length || 0, icon: '🚗' },
              { label: 'Pending', value: bookinglistdata.bookings?.filter(b => b.status === 'PENDING')?.length || 0, icon: '⏳' },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition-colors"
              >
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                <div className="text-sm text-gray-600">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </SlideIn>

      {/* Recent Activity */}
      <SlideIn direction="up" delay={0.6}>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t('recent_activity') || 'Recent Activity'}</h2>
            <a href="/bookings" className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1">
              View all <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          <div className="space-y-4">
            {(bookinglistdata.bookings || []).slice(0, 5).map((booking, index) => (
              <motion.div
                key={booking.id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Car className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{booking.customer_name || 'Customer'}</p>
                    <p className="text-sm text-gray-500">{booking.pickupAddress || 'N/A'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">
                    {settings?.symbol || '$'} {booking.trip_cost || '0'}
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    booking.status === 'COMPLETE' ? 'bg-green-100 text-green-700' :
                    booking.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {booking.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </SlideIn>
    </PageTransition>
  );
};

export default ModernDashboardView;
